import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, CheckCircle, ArrowLeft, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { useI18n } from '../lib/i18n';

const AGENT_NAME = 'IBRC';
const WHATSAPP_NUMBER = '5511925803469';
const WHATSAPP_DEFAULT_MSG = 'Olá, vim pelo site do IBRC!';

const isBusinessHours = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 6=Sat
  const hour = now.getHours();
  return day >= 1 && day <= 5 && hour >= 9 && hour < 17;
};

const getWhatsAppUrl = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg || WHATSAPP_DEFAULT_MSG)}`;

const saveWhatsAppLead = async (context = {}) => {
  try {
    const consent = localStorage.getItem('ibrc_cookie_consent');
    const geo = consent !== 'rejected'
      ? JSON.parse(sessionStorage.getItem('ibrc_geo') || '{}')
      : {};
    const utms = JSON.parse(sessionStorage.getItem('ibrc_utm') || '{}');
    const ua = navigator.userAgent;

    await supabase.from('contacts').insert({
      name: context.name || 'WhatsApp Lead',
      email: context.email || '',
      subject: context.program ? `whatsapp - ${context.program}` : `whatsapp - ${context.subject || 'direto'}`,
      message: context.message || null,
      phone: WHATSAPP_NUMBER,
      source: 'whatsapp_chat',
      visitor_id: localStorage.getItem('ibrc_vid') || null,
      session_id: sessionStorage.getItem('ibrc_sid') || null,
      device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
      browser: ua.includes('Firefox') ? 'Firefox' : ua.includes('Edg') ? 'Edge' : ua.includes('Chrome') ? 'Chrome' : ua.includes('Safari') ? 'Safari' : 'Other',
      os: ua.includes('Win') ? 'Windows' : ua.includes('Mac') ? 'macOS' : ua.includes('Android') ? 'Android' : ua.includes('iPhone') ? 'iOS' : 'Other',
      language: navigator.language,
      ip: geo.ip || null,
      country: geo.country || null,
      region: geo.region || null,
      city: geo.city || null,
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      page_path: window.location.pathname,
      referrer: document.referrer || null,
      metadata: {
        business_hours: isBusinessHours(),
        chat_phase: context.phase || 'unknown',
        timestamp: new Date().toISOString(),
        screen: `${window.screen.width}x${window.screen.height}`,
        pages_viewed: parseInt(sessionStorage.getItem('ibrc_pages_count') || '0', 10),
      },
    });
  } catch (_) {
    // Silent fail — don't block WhatsApp redirect
  }
};

const PROGRAMS = {
  a360: {
    name: 'A360',
    tag: { pt: 'Qualificação Global', en: 'Global Training' },
    desc: {
      pt: 'Até 792 horas de formação audiovisual gratuita com patrocínio Netflix, Amazon MGM Studios e Globoplay. Padrão internacional, do roteiro à pós-produção. 14 trilhas, 4 níveis de profundidade.',
      en: 'Up to 792 hours of free audiovisual training sponsored by Netflix, Amazon MGM Studios and Globoplay. International standard, from script to post-production. 14 tracks, 4 depth levels.',
    },
  },
  fabrica: {
    name: 'Fábrica de Conteúdos',
    tag: { pt: 'Estúdio-Escola', en: 'Studio-School' },
    desc: {
      pt: 'Pipeline de produção inspirado no cinema industrial. Documentários, séries, podcasts e milhares de horas de cursos produzidos. O aluno aprende fazendo, dentro de uma produção real.',
      en: 'Production pipeline inspired by industrial cinema. Documentaries, series, podcasts and thousands of hours of courses produced. Students learn by doing, inside a real production.',
    },
  },
  games: {
    name: 'Hub Sampa Games',
    tag: { pt: 'Games', en: 'Games' },
    desc: {
      pt: 'Polo de criação de jogos digitais em São Paulo. Formação em programação, game design, arte digital e narrativa interativa. Parceria com a Prefeitura de São Paulo.',
      en: 'Digital game creation hub in São Paulo. Training in programming, game design, digital art and interactive narrative. Partnership with the City of São Paulo.',
    },
  },
  amplificacine: {
    name: 'AmplificaCine',
    tag: { pt: 'Aceleração', en: 'Acceleration' },
    desc: {
      pt: 'R$50 mil por empresa acelerada. Programa de aceleração para negócios do audiovisual com mentoria em gestão, finanças, modelagem de negócios e pitch para investidores.',
      en: '$10K per accelerated company. Acceleration program for audiovisual businesses with mentoring in management, finance, business modeling and investor pitch.',
    },
  },
  sororidade: {
    name: 'Sororidade Conecta',
    tag: { pt: 'Empreendedorismo', en: 'Entrepreneurship' },
    desc: {
      pt: '500+ conteúdos exclusivos. Ecossistema phygital para empreendedorismo feminino com mentorias, networking e capacitação técnica e criativa.',
      en: '500+ exclusive contents. Phygital ecosystem for female entrepreneurship with mentoring, networking and technical/creative training.',
    },
  },
  publicus: {
    name: 'PUBLICUS',
    tag: { pt: 'Escola de IA', en: 'AI School' },
    desc: {
      pt: 'IA é bem público. Letramento em inteligência artificial, agent coding, governança agêntica e teoria crítica. Formação gratuita para quem quer entender e usar IA de verdade.',
      en: 'AI is a public good. AI literacy, agent coding, agentic governance and critical theory. Free training for those who want to truly understand and use AI.',
    },
  },
  cria: {
    name: 'CRIA',
    tag: { pt: 'Pesquisa', en: 'Research' },
    desc: {
      pt: 'ICT credenciada com acesso direto a FINEP, EMBRAPII e FNDCT. 4 linhas de pesquisa: IA aplicada à educação, economia criativa, tecnologias imersivas e impacto social.',
      en: 'Accredited ICT with direct access to FINEP, EMBRAPII and FNDCT. 4 research lines: AI applied to education, creative economy, immersive technologies and social impact.',
    },
  },
};

const ChatAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [phase, setPhase] = useState('greeting'); // greeting, programs, program-detail, collect-name, collect-email, collect-message, done
  const [data, setData] = useState({});
  const [typing, setTyping] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const { lang } = useI18n();

  const pt = lang === 'pt';

  const scrollToBottom = () => {
    setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const addAgent = (text, cb) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { from: 'agent', text }]);
      scrollToBottom();
      cb?.();
    }, 500 + Math.random() * 300);
  };

  const addUser = (text) => {
    setMessages((prev) => [...prev, { from: 'user', text }]);
    scrollToBottom();
  };

  // Init
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addAgent(pt
        ? `Olá! Sou o assistente do ${AGENT_NAME}. Como posso ajudar?`
        : `Hi! I'm the ${AGENT_NAME} assistant. How can I help?`
      );
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 600);
  }, [phase, isOpen]);

  // === HANDLERS ===

  const handleGreetingOption = (value) => {
    const labels = {
      programas: pt ? 'Conhecer programas' : 'Learn about programs',
      contato: pt ? 'Entrar em contato' : 'Get in touch',
      parceria: pt ? 'Parceria' : 'Partnership',
      outro: pt ? 'Outro assunto' : 'Other',
    };
    if (value === 'whatsapp') {
      addUser(pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp');
      trackEvent('chat_whatsapp_click', { category: 'engagement', phase: 'greeting' });
      saveWhatsAppLead({ subject: 'direto', phase: 'greeting' });
      if (isBusinessHours()) {
        addAgent(pt
          ? 'Abrindo o WhatsApp! Nossa equipe está online agora (seg-sex, 9h-17h) 💬'
          : 'Opening WhatsApp! Our team is online now (Mon-Fri, 9am-5pm) 💬'
        );
        setTimeout(() => window.open(getWhatsAppUrl(), '_blank'), 800);
      } else {
        addAgent(pt
          ? 'Nosso atendimento humano funciona de segunda a sexta, das 9h às 17h. Você pode enviar sua mensagem agora — respondemos no próximo dia útil! 💬'
          : 'Our team is available Mon-Fri, 9am-5pm. You can send a message now — we\'ll reply on the next business day! 💬',
          () => setTimeout(() => window.open(getWhatsAppUrl(
            pt ? 'Olá, vim pelo site do IBRC! (mensagem fora do horário comercial)' : 'Hi, I came from the IBRC website! (off-hours message)'
          ), '_blank'), 800)
        );
      }
      return;
    }

    addUser(labels[value]);
    setData((d) => ({ ...d, subject: value }));

    if (value === 'programas') {
      setPhase('programs');
      addAgent(pt
        ? 'Temos 7 programas incríveis! Qual te interessa?'
        : 'We have 7 amazing programs! Which interests you?'
      );
    } else {
      setPhase('collect-name');
      addAgent(pt ? 'Qual o seu nome?' : 'What\'s your name?');
    }
  };

  const handleProgramSelect = (key) => {
    const prog = PROGRAMS[key];
    addUser(prog.name);
    setData((d) => ({ ...d, program: prog.name }));
    setPhase('program-detail');
    addAgent(`**${prog.name}** — ${prog.tag[lang]}\n\n${prog.desc[lang]}`, () => {
      setTimeout(() => {
        addAgent(pt
          ? 'Quer saber mais sobre esse programa ou ver outro?'
          : 'Want to know more about this program or see another?'
        );
      }, 400);
    });
  };

  const handleDetailOption = (value) => {
    if (value === 'whatsapp') {
      addUser(pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp');
      const prog = data.program || '';
      trackEvent('chat_whatsapp_click', { category: 'engagement', phase: 'program-detail', program: prog });
      saveWhatsAppLead({ program: prog, subject: 'programas', phase: 'program-detail' });
      const msg = pt
        ? `Olá, vim pelo site do IBRC! Quero saber mais sobre o programa ${prog}.`
        : `Hi, I came from the IBRC website! I'd like to know more about ${prog}.`;
      if (isBusinessHours()) {
        addAgent(pt
          ? 'Abrindo o WhatsApp! Nossa equipe está online agora 💬'
          : 'Opening WhatsApp! Our team is online now 💬'
        );
        setTimeout(() => window.open(getWhatsAppUrl(msg), '_blank'), 800);
      } else {
        addAgent(pt
          ? 'Nosso atendimento funciona seg-sex, 9h-17h. Envie sua mensagem — respondemos no próximo dia útil! 💬'
          : 'Our team is available Mon-Fri, 9am-5pm. Send a message — we\'ll reply next business day! 💬',
          () => setTimeout(() => window.open(getWhatsAppUrl(msg), '_blank'), 800)
        );
      }
      return;
    }
    if (value === 'outro') {
      addUser(pt ? 'Ver outro programa' : 'See another program');
      setPhase('programs');
      addAgent(pt ? 'Qual programa te interessa?' : 'Which program interests you?');
    } else if (value === 'contato') {
      addUser(pt ? 'Quero saber mais' : 'I want to know more');
      setPhase('collect-name');
      addAgent(pt
        ? 'Ótimo! Deixa seu contato que nossa equipe fala com você. Qual seu nome?'
        : 'Great! Leave your contact and our team will reach out. What\'s your name?'
      );
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const value = input.trim();
    addUser(value);
    setInput('');

    if (phase === 'collect-name') {
      setData((d) => ({ ...d, name: value }));
      setPhase('collect-email');
      addAgent(pt ? `Prazer, ${value}! Qual seu e-mail?` : `Nice to meet you, ${value}! What's your email?`);
    } else if (phase === 'collect-email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        addAgent(pt ? 'Hmm, não parece um e-mail válido. Tenta de novo?' : 'Hmm, that doesn\'t look valid. Try again?');
        return;
      }
      setData((d) => ({ ...d, email: value }));
      setPhase('collect-message');
      addAgent(pt ? 'E o que gostaria de nos dizer? (ou "pular" para enviar)' : 'Anything you\'d like to tell us? (or "skip" to send)');
    } else if (phase === 'collect-message') {
      const skip = ['pular', 'skip', 'nao', 'não', 'no', ''].includes(value.toLowerCase());
      const finalData = { ...data, message: skip ? '' : value };
      setData(finalData);
      setPhase('done');
      addAgent(pt ? 'Enviando...' : 'Sending...', async () => {
        try {
          const consent = localStorage.getItem('ibrc_cookie_consent');
          const geo = consent !== 'rejected'
            ? JSON.parse(sessionStorage.getItem('ibrc_geo') || '{}')
            : {};
          const utms = JSON.parse(sessionStorage.getItem('ibrc_utm') || '{}');
          const ua = navigator.userAgent;

          await supabase.from('contacts').insert({
            name: finalData.name,
            email: finalData.email,
            subject: finalData.program ? `${finalData.subject} - ${finalData.program}` : finalData.subject,
            message: finalData.message || null,
            source: 'chat_widget',
            visitor_id: localStorage.getItem('ibrc_vid') || null,
            session_id: sessionStorage.getItem('ibrc_sid') || null,
            device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
            browser: ua.includes('Firefox') ? 'Firefox' : ua.includes('Edg') ? 'Edge' : ua.includes('Chrome') ? 'Chrome' : ua.includes('Safari') ? 'Safari' : 'Other',
            os: ua.includes('Win') ? 'Windows' : ua.includes('Mac') ? 'macOS' : ua.includes('Android') ? 'Android' : ua.includes('iPhone') ? 'iOS' : 'Other',
            language: navigator.language,
            ip: geo.ip || null,
            country: geo.country || null,
            region: geo.region || null,
            city: geo.city || null,
            utm_source: utms.utm_source || null,
            utm_medium: utms.utm_medium || null,
            utm_campaign: utms.utm_campaign || null,
            page_path: window.location.pathname,
            referrer: document.referrer || null,
            metadata: {
              chat_phase: 'collect-message',
              timestamp: new Date().toISOString(),
              screen: `${window.screen.width}x${window.screen.height}`,
              pages_viewed: parseInt(sessionStorage.getItem('ibrc_pages_count') || '0', 10),
            },
          });
          addAgent(pt
            ? `Pronto, ${finalData.name}! Sua mensagem foi enviada. Retornaremos em breve 🎬`
            : `Done, ${finalData.name}! Message sent. We'll get back to you soon 🎬`
          );
        } catch {
          addAgent(pt ? 'Ops, algo deu errado. Tente novamente.' : 'Oops, something went wrong.');
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const resetChat = () => {
    setMessages([]);
    setPhase('greeting');
    setData({});
    setInput('');
    setTimeout(() => addAgent(pt
      ? `Olá! Sou o assistente do ${AGENT_NAME}. Como posso ajudar?`
      : `Hi! I'm the ${AGENT_NAME} assistant. How can I help?`
    ), 300);
  };

  // === RENDER HELPERS ===

  const showGreetingOptions = phase === 'greeting' && messages.length === 1 && !typing;
  const showProgramOptions = phase === 'programs' && !typing;
  const showDetailOptions = phase === 'program-detail' && messages.length > 0 && !typing &&
    messages[messages.length - 1]?.text?.includes('?');
  const showInput = ['collect-name', 'collect-email', 'collect-message'].includes(phase);

  const greetingOptions = [
    { value: 'programas', label: pt ? 'Conhecer programas' : 'Programs' },
    { value: 'whatsapp', label: pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp', icon: 'whatsapp' },
    { value: 'contato', label: pt ? 'Entrar em contato' : 'Get in touch' },
    { value: 'parceria', label: pt ? 'Parceria' : 'Partnership' },
    { value: 'outro', label: pt ? 'Outro' : 'Other' },
  ];

  const detailOptions = [
    { value: 'contato', label: pt ? 'Quero saber mais' : 'Tell me more' },
    { value: 'whatsapp', label: pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp', icon: 'whatsapp' },
    { value: 'outro', label: pt ? 'Ver outro programa' : 'Another program' },
  ];

  const renderMessage = (text) => {
    // Simple markdown bold
    return text.split('**').map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => { if (!isOpen) trackEvent('chat_open', { category: 'engagement' }); setIsOpen(!isOpen); }}
        aria-label={isOpen ? (pt ? 'Fechar chat' : 'Close chat') : (pt ? 'Abrir chat' : 'Open chat')}
        className="fixed bottom-5 right-5 z-50 w-11 h-11 bg-black/50 backdrop-blur-md text-[#FFC107] flex items-center justify-center shadow-md hover:bg-[#FFC107]/80 hover:text-black transition-colors rounded-full"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
      >
        {isOpen ? <X size={18} aria-hidden="true" /> : <MessageCircle size={18} aria-hidden="true" />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="fixed bottom-[4.5rem] right-5 z-50 w-[340px] max-w-[calc(100vw-40px)] bg-white/95 backdrop-blur-xl border border-black/8 shadow-xl flex flex-col rounded-2xl overflow-hidden"
            style={{ height: '440px' }}
          >
            {/* Header */}
            <div className="bg-black/90 px-4 py-3 flex items-center gap-2.5 flex-shrink-0">
              <div className="w-6 h-6 bg-[#FFC107] rounded-sm flex items-center justify-center">
                <span className="font-oswald text-[9px] font-bold text-black">BR</span>
              </div>
              <div className="flex-1">
                <p className="font-oswald text-white text-xs uppercase tracking-wider">{AGENT_NAME}</p>
              </div>
              <span className="text-white/30 text-[9px] font-manrope">
                {typing ? (pt ? 'digitando...' : 'typing...') : 'online'}
              </span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-lenis-prevent>
              <AnimatePresence>
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] px-3 py-2 text-[13px] font-manrope leading-relaxed whitespace-pre-line ${
                      msg.from === 'user'
                        ? 'bg-[#FFC107] text-black rounded-2xl rounded-br-sm'
                        : 'bg-black/5 text-black/70 rounded-2xl rounded-bl-sm'
                    }`}>
                      {renderMessage(msg.text)}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Typing */}
              {typing && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-black/5 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <motion.div key={i} className="w-1.5 h-1.5 bg-black/30 rounded-full"
                        animate={{ y: [0, -3, 0] }} transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.15 }} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Greeting options */}
              {showGreetingOptions && (
                <Options items={greetingOptions} onSelect={(v) => handleGreetingOption(v)} />
              )}

              {/* Program list */}
              {showProgramOptions && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                  className="flex flex-col gap-1.5 pt-1">
                  {Object.entries(PROGRAMS).map(([key, prog]) => (
                    <motion.button key={key} onClick={() => handleProgramSelect(key)}
                      className="text-left px-3 py-2 border border-black/6 hover:bg-[#FFC107]/10 hover:border-[#FFC107]/30 transition-all rounded-xl group"
                      whileTap={{ scale: 0.98 }}>
                      <span className="font-oswald text-[12px] uppercase tracking-wider text-black/80 group-hover:text-black">
                        {prog.name}
                      </span>
                      <span className="block text-[10px] font-manrope text-black/35">{prog.tag[lang]}</span>
                    </motion.button>
                  ))}
                </motion.div>
              )}

              {/* Detail options */}
              {showDetailOptions && (
                <Options items={detailOptions} onSelect={(v) => handleDetailOption(v)} />
              )}

              {/* Done */}
              {phase === 'done' && !typing && messages[messages.length - 1]?.text?.includes('🎬') && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center py-3 gap-2">
                  <CheckCircle size={28} className="text-green-500" />
                  <motion.button
                    onClick={() => {
                      trackEvent('chat_whatsapp_click', { category: 'engagement', phase: 'done' });
                      saveWhatsAppLead({ name: data.name, email: data.email, subject: data.subject, program: data.program, message: data.message, phase: 'done' });
                      window.open(getWhatsAppUrl(
                        pt ? `Olá, sou ${data.name}. Acabei de enviar uma mensagem pelo site!` : `Hi, I'm ${data.name}. I just sent a message via the website!`
                      ), '_blank');
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-[11px] font-manrope hover:bg-[#1da851] transition-colors rounded-full"
                    whileTap={{ scale: 0.95 }}
                  >
                    <Phone size={12} />
                    {pt ? 'Continuar no WhatsApp' : 'Continue on WhatsApp'}
                  </motion.button>
                  <button onClick={resetChat} className="text-[10px] font-manrope text-black/30 hover:text-[#FFC107] underline">
                    {pt ? 'Nova conversa' : 'New conversation'}
                  </button>
                </motion.div>
              )}

              <div ref={messagesEnd} />
            </div>

            {/* Input */}
            {showInput && (
              <div className="border-t border-black/5 px-3 py-2.5 flex gap-2 flex-shrink-0">
                <input ref={inputRef}
                  type={phase === 'collect-email' ? 'email' : 'text'}
                  value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={
                    phase === 'collect-name' ? (pt ? 'Seu nome...' : 'Your name...') :
                    phase === 'collect-email' ? (pt ? 'seu@email.com' : 'your@email.com') :
                    (pt ? 'Sua mensagem...' : 'Your message...')
                  }
                  className="flex-1 bg-transparent outline-none font-manrope text-[13px] placeholder:text-black/25"
                />
                <motion.button onClick={handleSend}
                  className="w-8 h-8 bg-black/90 text-[#FFC107] flex items-center justify-center flex-shrink-0 hover:bg-[#FFC107] hover:text-black transition-colors rounded-full"
                  whileTap={{ scale: 0.9 }}>
                  <Send size={14} />
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Options = ({ items, onSelect }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
    className="flex flex-wrap gap-1.5 pt-1">
    {items.map((opt) => (
      <motion.button key={opt.value} onClick={() => onSelect(opt.value)}
        className={`px-3 py-1.5 border text-[12px] font-manrope transition-all rounded-full flex items-center gap-1.5 ${
          opt.icon === 'whatsapp'
            ? 'bg-[#25D366] border-[#25D366] text-white hover:bg-[#1da851]'
            : 'border-black/8 hover:bg-[#FFC107] hover:border-[#FFC107]'
        }`}
        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
        {opt.icon === 'whatsapp' && <Phone size={11} />}
        {opt.label}
      </motion.button>
    ))}
  </motion.div>
);

export default ChatAgent;
